import { ReactNode, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import styles from "./mobile-drawer.module.css";

interface MobileDrawerProps {
  children: ReactNode;
  isExpanded: boolean;
  onToggle: (expanded: boolean) => void;
}

const COLLAPSED_HEIGHT = 76;
const EXPANDED_HEIGHT_PERCENT = 0.65; // 65% of viewport height

export const MobileDrawer = ({
  children,
  isExpanded,
  onToggle,
}: MobileDrawerProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const expandedHeight =
    typeof window !== "undefined"
      ? window.innerHeight * EXPANDED_HEIGHT_PERCENT
      : 400;

  // Prevent pull-to-refresh and scroll passthrough
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const content = contentRef.current;
    if (!content) return;

    // If content is at the top and trying to scroll up, prevent default (pull-to-refresh)
    if (content.scrollTop <= 0) {
      const touch = e.touches[0];
      const startY = content.dataset.startY;
      if (startY && touch.clientY > parseFloat(startY)) {
        e.preventDefault();
      }
    }
    e.stopPropagation();
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const content = contentRef.current;
    if (content) {
      content.dataset.startY = e.touches[0].clientY.toString();
    }
  }, []);

  const handleDismiss = () => {
    onToggle(false);
  };

  return (
    <div className={styles.drawerWrapper}>
      {isExpanded && (
        <button className={styles.dismissButton} onClick={handleDismiss}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <motion.div
        className={styles.drawer}
        initial={false}
        drag={false}
        animate={{
          height: isExpanded ? expandedHeight : COLLAPSED_HEIGHT,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 300,
        }}
      >
        <div
        ref={contentRef}
        className={styles.content}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        {children}
      </div>
      </motion.div>
    </div>
  );
};

export { COLLAPSED_HEIGHT, EXPANDED_HEIGHT_PERCENT };
